import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useLiveQuery, useLiveQueryWithStatus } from "@/core/hooks/useLiveQuery";
import { ArrowLeft, Crosshair, RotateCw, Lock, LockOpen, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { offlineDb, type OfflineMesure } from "@/core/offline/db";
import { usePins } from "@/core/hooks/usePins";
import { useCampagneActive } from "@/core/hooks/useCampagneActive";
import { useViewedCampagne } from "@/core/hooks/useViewedCampagne";
import type { PinData } from "@/core/components/plan-viewer";

// Konva ne supporte pas le SSR — import lazy (pas besoin de ssr:false dans Vite SPA)
const PlanViewer = lazy(
  () => import("@/core/components/plan-viewer").then((m) => ({ default: m.PlanViewer }))
);

export default function ZoneDetailPage() {
  const { siteId, zoneId } = useParams<{ siteId: string; zoneId: string }>();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const viewedCampagneIdParam = searchParams.get("campagneId");

  const { data: zone, isLoading: zoneLoading } = useLiveQueryWithStatus(() => offlineDb.zones.get(zoneId!), [zoneId]);
  const { data: site, isLoading: siteLoading } = useLiveQueryWithStatus(() => offlineDb.sites.get(siteId!), [siteId]);
  const { campagne, campagnes, campagnesLoading, ensureCampagne } = useCampagneActive(siteId!);
  const { viewedCampagne, isViewingHistorical } = useViewedCampagne(siteId!);

  // Créer une campagne active au montage si aucune n'existe ET aucune clôturée non plus
  // (premier usage du site). Si des campagnes clôturées existent, on ne crée rien en
  // auto — l'utilisateur doit choisir explicitement "Nouvelle campagne" ou "Déverrouiller".
  // On attend que la query Dexie ait résolu pour éviter la race condition.
  useEffect(() => {
    if (!campagnesLoading && campagnes.length === 0) {
      ensureCampagne().catch((err) => console.error("[ZoneDetail] ensureCampagne:", err));
    }
  }, [siteId, campagnesLoading, campagnes.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ID de la campagne active (via useLiveQuery, toujours à jour)
  const campagneId = campagne?.id ?? null;

  // usePins au niveau zone — modèle B, les pins sont scopés site mais affichés par zone
  const { pins, createPin, movePin } = usePins(zoneId!);

  const [placementMode, setPlacementMode] = useState(false);
  const [planRotation, setPlanRotation] = useState<0 | 90 | 180 | 270>(zone?.planRotation ?? 0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  // Synchroniser la rotation quand la zone est chargée
  useEffect(() => {
    if (zone?.planRotation !== undefined) {
      setPlanRotation(zone.planRotation);
    }
  }, [zone?.planRotation]);

  // Mesurer le conteneur
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const rect = node.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    }
  }, []);

  // Resize listener
  useEffect(() => {
    function handleResize() {
      const el = document.getElementById("plan-container");
      if (el) {
        const rect = el.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // État "figé" = zone verrouillée OU site clôturé OU consultation historique
  const siteIsClosed = campagne?.status === "cloturee" || (!campagne && campagnes.length > 0);
  const zoneLocked = zone?.locked ?? false;
  const isReadOnly = isViewingHistorical || siteIsClosed || zoneLocked;

  async function handleTapOnPlan(x: number, y: number) {
    if (!placementMode || !campagneId || isReadOnly) return;
    await createPin(x, y);
  }

  async function toggleZoneLock() {
    if (!zoneId) return;
    await offlineDb.zones.update(zoneId, {
      locked: !zoneLocked,
      updatedAt: new Date(),
      syncStatus: "pending",
    });
  }

  function handlePinTap(pinId: string) {
    const suffix = viewedCampagneIdParam ? `?campagneId=${viewedCampagneIdParam}` : "";
    navigate(`/sites/${siteId}/zones/${zoneId}/pins/${pinId}${suffix}`);
  }

  async function handlePinDragEnd(pinId: string, x: number, y: number) {
    if (isReadOnly) return;
    await movePin(pinId, x, y);
  }

  // Charger les mesures de tous les pins POUR LA CAMPAGNE VISUALISÉE
  // (active par défaut, ou campagne historique si ?campagneId=X dans l'URL).
  const viewedCampagneId = viewedCampagne?.id;
  const allMesures = useLiveQuery<OfflineMesure[]>(
    async () => {
      const pinIds = pins.map((p) => p.id);
      if (pinIds.length === 0 || !viewedCampagneId) return [];
      return offlineDb.mesures
        .where("pinId")
        .anyOf(pinIds)
        .filter((m) => m.campagneId === viewedCampagneId)
        .toArray();
    },
    [pins, viewedCampagneId]
  ) ?? [];

  // Convertir les pins Dexie en PinData pour PlanViewer (avec couleur)
  const pinData: PinData[] = pins.map((pin) => {
    const mesure = allMesures.find((m) => m.pinId === pin.id);
    return {
      id: pin.id,
      x: pin.x,
      y: pin.y,
      number: pin.number,
      color: mesure?.codeCouleur ?? undefined,
    };
  });

  if (zoneLoading || siteLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <p className="text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (!zone || !site) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Zone introuvable.</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Retour
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col bg-background">
      {/* Header compact */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <button
          onClick={() => navigate(`/sites/${siteId}${viewedCampagneIdParam ? `?campagneId=${viewedCampagneIdParam}` : ""}`)}
          className="flex items-center text-muted-foreground"
        >
          <ArrowLeft className="size-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-semibold">{zone.name}</h1>
          <p className="truncate text-xs text-muted-foreground">
            {site.name}
            {viewedCampagne && (
              <>
                {" · "}
                {new Date(viewedCampagne.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
              </>
            )}
          </p>
        </div>
        <Badge variant="outline" className="shrink-0 text-xs">
          {pins.length} point{pins.length !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Bandeau consultation campagne historique */}
      {isViewingHistorical && viewedCampagne && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
          <Eye className="size-3.5 shrink-0" />
          <span className="flex-1 min-w-0">
            Campagne clôturée — mesures figées
          </span>
          <button
            className="text-primary underline shrink-0"
            onClick={() => navigate(`/sites/${siteId}/zones/${zoneId}`)}
          >
            Campagne actuelle
          </button>
        </div>
      )}

      {/* Zone du plan — prend tout l'espace disponible */}
      <div
        id="plan-container"
        ref={containerRef}
        className="relative flex-1 overflow-hidden"
      >
        {containerSize.width > 0 && containerSize.height > 0 && (
          <Suspense fallback={<div className="flex h-full items-center justify-center text-muted-foreground">Chargement du plan...</div>}>
            <PlanViewer
              planBlob={zone.planBlob}
              width={containerSize.width}
              height={containerSize.height}
              rotation={planRotation}
              pins={pinData}
              onTap={handleTapOnPlan}
              onPinTap={handlePinTap}
              onPinDragEnd={handlePinDragEnd}
              readOnly={isReadOnly}
            />
          </Suspense>
        )}

        {/* Indicateur mode placement */}
        {placementMode && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground shadow-md">
            Tapez sur le plan pour placer un point
          </div>
        )}
      </div>

      {/* Barre d'actions — pb-safe pour ne pas être cachée par la barre geste iOS */}
      <div className="flex items-center justify-between border-t border-border px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <div className="flex gap-2">
          {!isReadOnly && (
            <>
              <Button
                size="sm"
                variant={placementMode ? "default" : "outline"}
                onClick={() => setPlacementMode(!placementMode)}
              >
                <Crosshair className="size-4" />
                {placementMode ? "Terminer" : "Placer"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const next = ((planRotation + 90) % 360) as 0 | 90 | 180 | 270;
                  setPlanRotation(next);
                  if (zoneId) {
                    offlineDb.zones.update(zoneId, { planRotation: next }).catch(() => {});
                  }
                }}
                title="Tourner le plan"
              >
                <RotateCw className="size-4" />
              </Button>
            </>
          )}
          {siteIsClosed && (
            <Badge variant="secondary" className="text-xs">
              <Lock className="mr-1 size-3" />
              Site clôturé
            </Badge>
          )}
          {!siteIsClosed && zoneLocked && (
            <Badge variant="secondary" className="text-xs">
              <Lock className="mr-1 size-3" />
              Zone verrouillée
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {campagne
              ? `${campagne.date.toLocaleDateString("fr-FR")}`
              : ""}
          </span>
          {!siteIsClosed && (
            <Button
              size="sm"
              variant="outline"
              onClick={toggleZoneLock}
              className="text-xs"
              title={zoneLocked ? "Déverrouiller la zone" : "Verrouiller la zone pour éviter les modifs accidentelles"}
            >
              {zoneLocked ? <LockOpen className="size-3" /> : <Lock className="size-3" />}
              {zoneLocked ? "Déverrouiller" : "Verrouiller"}
            </Button>
          )}
        </div>
      </div>

    </div>
  );
}
